import React, { useEffect, useMemo, useRef, useState } from 'react'
import MarquesRefMoto from "./MarquesRefMoto"
import MarquesRefLoisir from "./MarquesRefLoisir"
import lodash, { cloneDeep } from "lodash"
import { Site } from "../../../types/Site"
import ModalTokenKifal from "./ModalTokenKifal"
import { GpParam } from "../../../types/GpParam"
import BackOfficeAPIClient from "../../../clients/BackofficeAPIClient"
import ModalConfigAutobiz from "./ModalConfigAutobiz"
import useSocietes from "../../../hooks/useSocietes"
import { CATALOG_KEYS, CatalogKey } from "../../../types/Catalog"

declare const window: any

export type MarquesFormRef = {
    submit: () => Promise<void> | void;
};

type Activite = {
    IDActivite: number
    Descr: string
}

export default function Catalogues() {

    const { societes, fetchSocietes } = useSocietes()

    const backofficeApiClient = new BackOfficeAPIClient()

    const catalogColumnNames = CATALOG_KEYS

    const marquesRefMotoRef = useRef<MarquesFormRef>(null)
    const marquesRefLoisirRef = useRef<MarquesFormRef>(null)

    const [ sites, setSites ] = useState<Site[]>([])
    const [ activites, setActivites ] = useState<Activite[]>([])
    const [ updatedSiteIds, setUpdatedSiteIds ] = useState<number[]>([])
    const [ submitting, setSubmitting ] = useState<boolean>(false)
    const [ refMotoSelectedMarqueCodes, setRefMotoSelectedMarqueCodes ] = useState<string[]>([])
    const [ refMotoSelectedMarqueCodesUpdated, setRefMotoSelectedMarqueCodesUpdated ] = useState<boolean>(false)
    const [ refLoisirSelectedMarqueCodes, setRefLoisirSelectedMarqueCodes ] = useState<string[]>([])
    const [ refLoisirSelectedMarqueCodesUpdated, setRefLoisirSelectedMarqueCodesUpdated ] = useState<boolean>(false)
    const [ selectedTab, setSelectedTab ] = useState<"automobile" | "moto" | "vdl">("automobile")
    const [ gpParams, setGpParams ] = useState<GpParam>()
    const [ gpParamsForm, setGpParamsForm ] = useState<GpParam>()

    useEffect(() => {
        fetchSites()
        fetchActivites()
        fetchGpParams()
    }, [])

    const refMotoActif = useMemo(() => {
        return lodash.some(sites, (s: Site) => s.RefMotoActif)
    }, [ sites ])

    const refLoisirActif = useMemo(() => {
        return lodash.some(sites, (s: Site) => s.RefVdlActif)
    }, [ sites ])

    const onChangeRefMotoSelectedMarques = (selectedMarqueCodes: string[]) => {
        setRefMotoSelectedMarqueCodes(selectedMarqueCodes)
        setRefMotoSelectedMarqueCodesUpdated(true)
    }

    const onChangeRefLoisirSelectedMarques = (selectedMarqueCodes: string[]) => {
        setRefLoisirSelectedMarqueCodes(selectedMarqueCodes)
        setRefLoisirSelectedMarqueCodesUpdated(true)
    }

    const updateRefMotoSelectedMarques = (selectedMarqueCodes: string[]) => {
        setRefMotoSelectedMarqueCodes(selectedMarqueCodes)
    }

    const updateRefLoisirSelectedMarques = (selectedMarqueCodes: string[]) => {
        setRefLoisirSelectedMarqueCodes(selectedMarqueCodes)
    }

    const onChange = (IDSite: number, catalogColumnName: CatalogKey) => {
        if (!catalogColumnNames.includes(catalogColumnName)) {
            return
        }
        const sitesUpdated: Site[] = cloneDeep(sites)
        const idx: number = lodash.findIndex(sitesUpdated, (site: Site) => site.IDSite == IDSite)

        if (idx === -1) {
            return
        }

        sitesUpdated[idx][catalogColumnName] = !sites[idx][catalogColumnName]

        if (!updatedSiteIds.includes(IDSite)) {
            updatedSiteIds.push(IDSite)
        }

        setUpdatedSiteIds(updatedSiteIds)
        setSites(sitesUpdated)
    }

    const onSubmit = async () => {
        if (refMotoActif && refMotoSelectedMarqueCodes.length === 0) {
            alert("Le Référentiel Moto est activé pour au moins un site : vous devez configurer au moins une marque")
            return
        }
        if (refLoisirActif && refLoisirSelectedMarqueCodes.length === 0) {
            alert("Le Référentiel Loisir est activé pour au moins un site : vous devez configurer au moins une marque")
            return
        }

        setSubmitting(true)

        try {
            const updatedSitesData = lodash.filter(sites, (s: Site) => updatedSiteIds.includes(s.IDSite)).map((s: Site) => {
                return {
                    "IDSite": s.IDSite,
                    "AutovistaActif": s.AutovistaActif,
                    "RefMotoActif": s.RefMotoActif,
                    "RefVdlActif": s.RefVdlActif,
                    "KifalActif": s.KifalActif,
                    "AutobizActif": s.AutobizActif,
                }
            })
            await backofficeApiClient.patch('/sites/catalogues', {
                "sites": updatedSitesData,
            })

            setUpdatedSiteIds([])

            if (refMotoActif) {
                try {
                    if (marquesRefMotoRef.current) {
                        await marquesRefMotoRef.current.submit()
                    }
                    setRefMotoSelectedMarqueCodesUpdated(false)
                } catch (error) {
                    alert("Erreur lors de la synchronisation des marques actives sur le Référentiel Moto")
                    console.log(error)
                }
            }

            if (refLoisirActif) {
                try {
                    if (marquesRefLoisirRef.current) {
                        await marquesRefLoisirRef.current.submit()
                    }
                    setRefLoisirSelectedMarqueCodesUpdated(false)
                } catch (error) {
                    alert("Erreur lors de la synchronisation des marques actives sur le Référentiel Loisir")
                    console.log(error)
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            setSubmitting(false)
        }
    }

    const fetchSites = async () => {
        try {
            let sites = await backofficeApiClient.get<Site[]>("/sites")

            sites = lodash.sortBy(sites, (site: Site) => {
                return site.Plaque_Libelle
            })

            setSites(sites)
        } catch (error) {
            console.log(error)
        }
    }

    const fetchGpParams = async () => {
        try {
            const json = await backofficeApiClient.get<GpParam>("/gpParametres")

            setGpParams(json)
            setGpParamsForm(json)
        } catch (error) {
            console.log(error)
        }
    }

    const fetchActivites = async () => {
        try {
            const json = await backofficeApiClient.get<Activite[]>("/activites")

            setActivites(json)
        } catch (error) {
            console.log(error)
        }
    }

    const IDActiviteAutomobile = useMemo(() => {
        const activite = lodash.find(activites, (a: Activite) => a.Descr === "Automobile")
        return activite ? activite.IDActivite : null
    }, [ activites ])

    const IDActiviteMoto = useMemo(() => {
        const activite = lodash.find(activites, (a: Activite) => a.Descr === "Moto")
        return activite ? activite.IDActivite : null
    }, [ activites ])

    const IDActiviteVDL = useMemo(() => {
        const activite = lodash.find(activites, (a: Activite) => a.Descr === "Loisirs")
        return activite ? activite.IDActivite : null
    }, [ activites ])

    const isKifalDisabled = useMemo(() => {
        const service = gpParams?.GRP_KifalServiceToken
        const cotation = gpParams?.GRP_KifalCotationToken

        // Disable si au moins un des deux est vide | null | undefined
        return !service || !cotation
    }, [ gpParams ])

    const isAutobizCheckboxDisabled = (companyId: number): boolean => {
        const societe = societes.find(s => s.IDPlaque === companyId)
        if (!societe) return true
        return !societe.autobiz_username || !societe.autobiz_password
    }

    useEffect(() => { window.$('[data-toggle="tooltip"]').tooltip()})

    return (
        <>
            <div className="container-fluid mb-2">
                <div className="row">
                    <div className="col">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h2>Catalogues</h2>
                            <div>
                                {updatedSiteIds.length > 0 || refMotoSelectedMarqueCodesUpdated || refLoisirSelectedMarqueCodesUpdated ?
                                    <div className="d-flex flex-row">
                                        <div className="alert alert-warning m-0 p-1 mr-1">
                                            <i className="fa fa-warning mr-1"></i>
                                            Modifications non sauvegardées
                                        </div>
                                        <button type="submit" className="btn btn-primary m-0 p-1" onClick={onSubmit}>
                                            {submitting && <i className="fa fa-spinner fa-spin fa-fw mr-1"></i>}
                                            Valider
                                        </button>
                                    </div>
                                    : null}
                            </div>
                        </div>

                        <div className="alert alert-danger" role="alert"><h4 className="alert-heading">Attention !</h4>L'activation d'un site est soumis à facturation, merci de vous rapprocher du
                            service
                            commerce si le client souhaite faire évoluer sa configuration.
                        </div>
                        <ul className="nav nav-tabs" id="myTab">
                            <li className="nav-item">
                                <a onClick={() => setSelectedTab("automobile")} className="nav-link active" id="automobile-tab" data-toggle="tab" href="#automobile" role="tab"
                                   aria-controls="automobile"
                                   aria-selected="true">Automobile</a>
                            </li>
                            <li className="nav-item" key="sites-tab-renault">
                                <a onClick={() => setSelectedTab("moto")} className="nav-link" id="moto-tab" data-toggle="tab" href="#moto" role="tab" aria-controls="moto"
                                   aria-selected="true">Moto</a>
                            </li>
                            <li className="nav-item" key="sites-tab-stellantis">
                                <a onClick={() => setSelectedTab("vdl")} className="nav-link" id="vdl-tab" data-toggle="tab" href="#vdl" role="tab" aria-controls="vdl" aria-selected="true">VDL</a>
                            </li>
                        </ul>
                        <div className="tab-content" id="myTabContent" style={{ paddingTop: '15px' }}>
                            <div className="tab-pane fade show active" id="automobile" role="tabpanel" aria-labelledby="automobile-tab"></div>
                            <div className="tab-pane fade" id="moto" role="tabpanel" aria-labelledby="moto-tab">
                                <div className="d-flex">
                                    {refMotoActif &&
                                        <MarquesRefMoto ref={marquesRefMotoRef as any}
                                                        onChangeMarques={onChangeRefMotoSelectedMarques}
                                                        updateRefMotoSelectedMarques={updateRefMotoSelectedMarques}
                                        />
                                    }
                                </div>
                            </div>
                            <div className="tab-pane fade" id="vdl" role="tabpanel" aria-labelledby="vdl-tab">
                                <div className="d-flex">
                                    {refLoisirActif &&
                                        <MarquesRefLoisir ref={marquesRefLoisirRef as any}
                                                          onChangeMarques={onChangeRefLoisirSelectedMarques}
                                                          updateRefLoisirSelectedMarques={updateRefLoisirSelectedMarques}
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                        <table className="table table-striped">
                            <thead>
                            <tr>
                                <th scope="col">Société</th>
                                <th scope="col">Site</th>
                                {selectedTab === "automobile" &&
                                    <>
                                        <th scope="col" className="text-center">Autobiz
                                            <i data-toggle="modal" data-target="#modalConfigAutobiz" className="fa fa-pen tw-cursor-pointer" style={{ fontSize: '1em', marginLeft: 10 }}/>
                                        </th>
                                        <th scope="col" className="text-center">Autovista</th>
                                        <th scope="col" className="text-center">Kifal
                                            {isKifalDisabled ?
                                                <i key={"isError"} data-toggle="tooltip" data-trigger="hover" data-placement="bottom" data-html="true"
                                                   title={"Un ou plusieurs token ne sont pas configurés"}
                                                   className="fa fa-warning text-warning"
                                                   style={{ fontSize: '1em', marginLeft: 10 }}/>
                                                : <i key={"isSuccess"} className="fa fa-check-circle text-success" style={{ fontSize: '1em', marginLeft: 10 }}/>
                                            }
                                            <i data-toggle="modal" data-target="#modalKifalToken" className="fa fa-pen tw-cursor-pointer" style={{ fontSize: '1em', marginLeft: 10 }}/>
                                        </th>
                                    </>
                                }
                                {selectedTab === "moto" &&
                                    <>
                                        <th scope="col" className="text-center">Référentiel Moto</th>
                                    </>
                                }
                                {selectedTab === "vdl" &&
                                    <>
                                        <th scope="col" className="text-center">Référentiel VDL</th>
                                    </>
                                }
                            </tr>
                            </thead>
                            <tbody>
                            {sites.filter((s: Site) => {
                                const IDActivite = s.Plaque_IDActivite ? s.Plaque_IDActivite : s.IDActivite
                                if (selectedTab === "automobile") {
                                    return IDActivite === IDActiviteAutomobile
                                }
                                if (selectedTab === "moto") {
                                    return IDActivite === IDActiviteMoto
                                }
                                if (selectedTab === "vdl") {
                                    return IDActivite === IDActiviteVDL
                                }
                            }).map((site: any) => {
                                const isDisabledCheckbox = isAutobizCheckboxDisabled(site.IDPlaque)
                                return (<tr key={site.IDSite}>
                                    <td>{site.Plaque_Libelle}</td>
                                    <td>{site.Descr}</td>
                                    {selectedTab === "automobile" &&
                                        <>
                                            <td className="text-center">
                                                <input
                                                    onChange={() => onChange(site.IDSite, "AutobizActif")}
                                                    type="checkbox"
                                                    checked={site.AutobizActif == 1}
                                                    disabled={isDisabledCheckbox}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    onChange={() => onChange(site.IDSite, "AutovistaActif")}
                                                    type="checkbox"
                                                    checked={site.AutovistaActif == 1}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    disabled={isKifalDisabled}
                                                    onChange={() => onChange(site.IDSite, "KifalActif")}
                                                    type="checkbox"
                                                    checked={site.KifalActif == 1}
                                                />
                                            </td>
                                        </>
                                    }
                                    {selectedTab === "moto" &&
                                        <>
                                            <td className="text-center">
                                                <input
                                                    onChange={() => onChange(site.IDSite, "RefMotoActif")}
                                                    type="checkbox"
                                                    checked={site.RefMotoActif == 1}
                                                />
                                            </td>
                                        </>
                                    }
                                    {selectedTab === "vdl" &&
                                        <>
                                            <td className="text-center">
                                                <input
                                                    onChange={() => onChange(site.IDSite, "RefVdlActif")}
                                                    type="checkbox"
                                                    checked={site.RefVdlActif == 1}
                                                />
                                            </td>
                                        </>
                                    }
                                </tr>)
                            })}
                            </tbody>
                        </table>

                        <div className="row mt-4">
                            <div className="col text-center">
                                <button disabled={updatedSiteIds.length === 0 && !refMotoSelectedMarqueCodesUpdated && !refLoisirSelectedMarqueCodesUpdated}
                                        type="submit"
                                        className="btn btn-primary" onClick={onSubmit}>
                                    {submitting && <i className="fa fa-spinner fa-spin fa-fw mr-1"></i>}
                                    Valider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ModalTokenKifal
                gpParamsForm={gpParamsForm}
                gpParams={gpParams}
                setGpParamsForm={setGpParamsForm}
                fetchGpParams={fetchGpParams}
            />
            <ModalConfigAutobiz
                societes={societes}
                callback={()=>{
                    fetchSites()
                    fetchSocietes()
                }}
            />
        </>
    )
}